/**
 * DSD 프리뷰의 방향키 navigation 훅.
 *
 * - 방향키(Shift 없는 ↑↓←→) — 하이라이트 셀들 사이 이동. Shift+방향키는 IXD navigation 전용.
 * - 좌/우: 같은 행 내 한 칸 이동. 행 끝에서 다음 행의 첫 셀(↑ 이전 행 마지막) 로 wrap 되어
 *   reading order 로 흐른다.
 * - 위/아래: 같은 표 안에서 아래(위) 행들을 순서대로 스캔해 현재 셀과 수평으로 겹치는
 *   첫 셀(overlap 이 가장 큰 후보) 을 고른다. 바로 인접한 행에 겹치는 셀이 없으면(예:
 *   바로 아래가 비하이라이트 셀) 그 행을 건너뛰고 계속 내려간다. 열 바닥에 닿으면 다음
 *   열의 최상단(↑ 는 이전 열의 최하단) 으로, 표의 마지막 열이면 다음 표로 wrap 되어
 *   열-우선으로 흐른다. 스캔과 열 wrap 은 표 경계를 넘지 않는다.
 *
 * 행 그룹핑은 시각적 y 좌표가 아니라 부모 <tr> 의 element identity 로 한다. y 기반
 * 휴리스틱은 rowspan / 다양한 셀 높이 / 표 간 간격 등 때문에 같은 <tr> 의 셀이 다른
 * 행으로 갈라지거나, 다른 <tr> 의 셀이 같은 행으로 잘못 묶이는 일이 잦다.
 *
 * document capture phase 에 핸들러를 등록한다. IXD 셀이 선택된 상태에서는 IXD selection
 * 이동을 우선해야 하므로 키를 그대로 통과시켜 bubble phase 의 IXD 리스너가 처리하게 둔다.
 * IXD 선택이 없는 경우에만 키를 소비하며 stopImmediatePropagation 으로 잠재적 IXD
 * 리스너의 실행을 막는다.
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type Dispatch,
  type RefObject,
  type SetStateAction,
} from "react";
import type { DSDSubChunkInfo, DsdParsedCell } from "@/apis/compare/route";
import { ATTR } from "@/utils/dom-attrs";
import { hasActiveIxdSelection } from "@/components/ixd-preview";
import {
  applyFocusScroll,
  planFocusScroll,
} from "@/utils/ixd-dsd-compare/focus-utils";

type ArrowDirection = "up" | "down" | "left" | "right";

export interface RowGridCell {
  dsdId: string;
  rect: DOMRect;
  tr: HTMLTableRowElement;
  table: Element;
}

export type RowGrid = RowGridCell[][];

const arrowKeyToDirection = (key: string): ArrowDirection | null => {
  switch (key) {
    case "ArrowUp":
      return "up";
    case "ArrowDown":
      return "down";
    case "ArrowLeft":
      return "left";
    case "ArrowRight":
      return "right";
    default:
      return null;
  }
};

const isEditableTarget = (target: EventTarget | null): boolean => {
  if (target instanceof HTMLInputElement) return true;
  if (target instanceof HTMLTextAreaElement) return true;
  if (target instanceof HTMLElement && target.isContentEditable) return true;
  return false;
};

/**
 * 하이라이트(=matched/negated/should-tag) 셀들을 렌더 순서대로 평탄화한다.
 * originCells 가 1개뿐인 표는 paragraph 로 렌더되므로 navigation 대상에서 제외한다.
 */
const collectHighlightedCells = (
  subChunks: DSDSubChunkInfo[],
): DsdParsedCell[] => {
  const list: DsdParsedCell[] = [];
  for (const subChunk of subChunks) {
    for (const block of subChunk.parsedContent ?? []) {
      if (block.blockType !== "table" || !block.cells) continue;
      const originCells = block.cells
        .flat()
        .filter((c): c is DsdParsedCell => c != null && c.isOrigin);
      if (originCells.length <= 1) continue;
      for (const cell of originCells) {
        if (cell.matchStatus && cell.matchStatus !== "default") {
          list.push(cell);
        }
      }
    }
  }
  return list;
};

/** 현재 렌더된 후보 셀들을 부모 <tr> 별로 묶어 시각적 순서로 정렬한 row grid 를 만든다. */
const buildRowGrid = (
  container: HTMLElement,
  cells: DsdParsedCell[],
): RowGrid => {
  const candidates: RowGridCell[] = [];
  for (const c of cells) {
    const el = container.querySelector<HTMLElement>(
      `[${ATTR.cellId}="${CSS.escape(c.dsdId)}"]`,
    );
    if (!el) continue;
    const tr = el.closest("tr");
    if (!tr) continue;
    candidates.push({
      dsdId: c.dsdId,
      rect: el.getBoundingClientRect(),
      tr,
      table: tr.closest("table") ?? tr,
    });
  }
  if (candidates.length === 0) return [];

  const rowsMap = new Map<HTMLTableRowElement, RowGridCell[]>();
  for (const c of candidates) {
    const bucket = rowsMap.get(c.tr);
    if (bucket) bucket.push(c);
    else rowsMap.set(c.tr, [c]);
  }
  return Array.from(rowsMap.values())
    .sort((a, b) => a[0].rect.top - b[0].rect.top)
    .map((row) => row.sort((a, b) => a.rect.left - b.rect.left));
};

const overlapWidth = (a: DOMRect, b: DOMRect): number =>
  Math.min(a.right, b.right) - Math.max(a.left, b.left);

/**
 * Up/Down 스캔 시 한 행에서 열을 매칭한다.
 * 현재 셀과 수평 구간이 겹치는 후보 중 overlap 이 가장 큰 셀. 겹치는 셀이 없으면 null —
 * 중심-x 최근접 fallback 은 시각적으로 다른 열로 점프해 보여서 두지 않는다.
 */
const pickOverlapMatch = (
  row: RowGridCell[],
  curRect: DOMRect,
): RowGridCell | null => {
  let best: RowGridCell | null = null;
  let bestWidth = 0;
  for (const c of row) {
    const overlap = overlapWidth(c.rect, curRect);
    if (overlap > bestWidth) {
      best = c;
      bestWidth = overlap;
    }
  }
  return best;
};

/**
 * 열 wrap 목적지: 후보 중 side 방향으로 가장 앞선 열을 찾아 그 열의 edge 셀을 고른다.
 * "열" 은 기준 셀과 수평으로 겹치는 셀들의 집합으로 정의한다 (colspan/폭 차이 허용).
 */
const pickColumnEdgeCell = (
  cells: RowGridCell[],
  side: "left" | "right",
  edge: "top" | "bottom",
): RowGridCell => {
  const ref = cells.reduce((best, c) =>
    side === "left"
      ? c.rect.left < best.rect.left
        ? c
        : best
      : c.rect.right > best.rect.right
        ? c
        : best,
  );
  const column = cells.filter((c) => overlapWidth(c.rect, ref.rect) > 0);
  return column.reduce((best, c) =>
    edge === "top"
      ? c.rect.top < best.rect.top
        ? c
        : best
      : c.rect.bottom > best.rect.bottom
        ? c
        : best,
  );
};

const findGridPosition = (
  grid: RowGrid,
  dsdId: string | null,
): { row: number; col: number } | null => {
  if (!dsdId) return null;
  for (let r = 0; r < grid.length; r += 1) {
    const idx = grid[r].findIndex((c) => c.dsdId === dsdId);
    if (idx !== -1) return { row: r, col: idx };
  }
  return null;
};

/**
 * 방향키 + 현재 위치(혹은 미포커스) 로 다음 타깃 dsdId 를 계산한다.
 * 이동할 곳이 없으면 null 을 반환해 키 이벤트를 소비하지 않도록 한다.
 */
export const computeTargetDsdId = (
  grid: RowGrid,
  current: { row: number; col: number } | null,
  dir: ArrowDirection,
): string | null => {
  if (grid.length === 0) return null;

  // 미포커스 진입: 방향에 맞는 끝점에서 시작.
  if (!current) {
    const lastRow = grid[grid.length - 1];
    return dir === "up" || dir === "left"
      ? lastRow[lastRow.length - 1].dsdId
      : grid[0][0].dsdId;
  }

  const { row, col } = current;
  const cur = grid[row][col];
  const curRect = cur.rect;

  switch (dir) {
    case "right":
      if (col + 1 < grid[row].length) return grid[row][col + 1].dsdId;
      if (row + 1 < grid.length) return grid[row + 1][0].dsdId;
      return null;
    case "left":
      if (col - 1 >= 0) return grid[row][col - 1].dsdId;
      if (row - 1 >= 0) {
        const prev = grid[row - 1];
        return prev[prev.length - 1].dsdId;
      }
      return null;
    case "down":
    case "up": {
      const step = dir === "down" ? 1 : -1;

      // 1) 같은 표의 진행 방향 행들을 스캔해 수평으로 겹치는 첫 셀로 이동.
      for (let r = row + step; r >= 0 && r < grid.length; r += step) {
        if (grid[r][0].table !== cur.table) continue;
        const match = pickOverlapMatch(grid[r], curRect);
        if (match) return match.dsdId;
      }

      // 2) 열 끝: 같은 표 안에서 다음(이전) 열의 최상단(최하단) 셀로 wrap.
      const tableCells = grid.flat().filter((c) => c.table === cur.table);
      const wrapCandidates = tableCells.filter((c) =>
        dir === "down"
          ? c.rect.left >= curRect.right
          : c.rect.right <= curRect.left,
      );
      if (wrapCandidates.length > 0) {
        return dir === "down"
          ? pickColumnEdgeCell(wrapCandidates, "left", "top").dsdId
          : pickColumnEdgeCell(wrapCandidates, "right", "bottom").dsdId;
      }

      // 3) 표의 마지막(첫) 열: 다음(이전) 표의 첫 열 최상단(마지막 열 최하단) 셀로.
      const tables: Element[] = [];
      for (const r of grid) {
        if (!tables.includes(r[0].table)) tables.push(r[0].table);
      }
      const nextTable = tables[tables.indexOf(cur.table) + step];
      if (!nextTable) return null;
      const nextCells = grid.flat().filter((c) => c.table === nextTable);
      return dir === "down"
        ? pickColumnEdgeCell(nextCells, "left", "top").dsdId
        : pickColumnEdgeCell(nextCells, "right", "bottom").dsdId;
    }
  }
};

export interface UseDsdCellNavigationOptions {
  containerRef: RefObject<HTMLDivElement | null>;
  subChunks: DSDSubChunkInfo[];
  /** 외부에서 focus 를 초기화시키고 싶을 때 변경하는 키 */
  focusedDsdResetKey?: number;
  /** navigation 이 도달한 셀에 대한 dispatcher (matched / should-tag / no-match 분기 등) */
  onCellInteract: (cell: DsdParsedCell) => void;
}

export interface UseDsdCellNavigationResult {
  focusedDsdId: string | null;
  setFocusedDsdId: Dispatch<SetStateAction<string | null>>;
}

export const useDsdCellNavigation = ({
  containerRef,
  subChunks,
  focusedDsdResetKey,
  onCellInteract,
}: UseDsdCellNavigationOptions): UseDsdCellNavigationResult => {
  const [focusedDsdId, setFocusedDsdId] = useState<string | null>(null);

  useEffect(() => {
    setFocusedDsdId(null);
  }, [focusedDsdResetKey]);

  const highlightedCells = useMemo(
    () => collectHighlightedCells(subChunks),
    [subChunks],
  );

  /**
   * focus 갱신 + interact dispatch + 화면 스크롤 — 모두 묶어 처리.
   *
   * 스크롤은 셀이 안 보일 때만, 보일 만큼만 한다. 표 기준선(`sectionSelector`)은
   * 주지 않는다 — 한 칸씩 옮기는 조작에서 표 경계를 넘을 때마다 표 머리로 점프하면
   * 커서 이동 감각이 깨진다.
   */
  const navigateToDsdId = useCallback(
    (dsdId: string) => {
      const cell = highlightedCells.find((c) => c.dsdId === dsdId);
      if (!cell) return;
      setFocusedDsdId(cell.dsdId);
      onCellInteract(cell);
      requestAnimationFrame(() => {
        const container = containerRef.current;
        const target = container?.querySelector<HTMLElement>(
          `[${ATTR.cellId}="${CSS.escape(cell.dsdId)}"]`,
        );
        if (!container || !target) return;
        applyFocusScroll(
          planFocusScroll({ cell: target, prevCell: null, container }),
        );
      });
    },
    [highlightedCells, onCellInteract, containerRef],
  );

  useEffect(() => {
    if (highlightedCells.length === 0) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.shiftKey || e.metaKey || e.ctrlKey || e.altKey) return;
      if (hasActiveIxdSelection()) return;
      if (isEditableTarget(e.target)) return;

      const dir = arrowKeyToDirection(e.key);
      if (!dir) return;

      const container = containerRef.current;
      if (!container) return;

      const grid = buildRowGrid(container, highlightedCells);
      if (grid.length === 0) return;

      const current = findGridPosition(grid, focusedDsdId);
      const targetDsdId = computeTargetDsdId(grid, current, dir);
      if (!targetDsdId) return;

      e.preventDefault();
      e.stopImmediatePropagation();
      navigateToDsdId(targetDsdId);
    };

    document.addEventListener("keydown", handleKeyDown, { capture: true });
    return () =>
      document.removeEventListener("keydown", handleKeyDown, {
        capture: true,
      });
  }, [highlightedCells, focusedDsdId, navigateToDsdId, containerRef]);

  return { focusedDsdId, setFocusedDsdId };
};
